// components/chat/DirectMessageThread.jsx
import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { CheckCircle, Tag as TagIcon, Phone, Video, Download, FileText, FileImage, FileVideo, Mail, File as FileIcon, AlertTriangle } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { useTaggedMessages } from "../../context/TaggedMessagesContext";
import MessageInputBox from "./MessageInputBox";
import CallThreadEntry from "./CallThreadEntry";
import KudosBanner from "../staffdashboard/KudosBanner";
import { toast } from "../ui/Toast";
import { isRecordingLink, openRecordingsPanel } from "./recordingLink";

const NEON = "#00FF99";
const TAG_COLORS = {
  leave:"#facc15", ticket:"#60a5fa", mood:"#c084fc",
  payslip:"#34d399", learning:"#fb923c", correction:"#f87171", urgent:"#ef4444",
};
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const WS_BASE  = API_BASE.replace("https://","wss://").replace("http://","ws://");

// Header action-button hover (messenger_a2 spec .ib): green icon + green-line
// border on hover — matches the conversation-pane mode switcher's hover idiom.
const IB_CSS = `.kw-ib:hover{color:#00FF99 !important;border-color:rgba(0,255,153,0.30) !important}`;
const IB_STYLE = {
  width:        34,
  height:       34,
  borderRadius: 11,
  background:   "#0d0d0d",
  border:       "1px solid rgba(255,255,255,0.10)",
  display:      "grid",
  placeItems:   "center",
  cursor:       "pointer",
  padding:      0,
  color:        "rgba(255,255,255,0.55)",
  transition:   "all 0.15s",
};

const ini      = n => {
  const p = (n || "?").trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "?") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
};
const niceTime = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
};
// Date-divider helpers — derived entirely from each message's existing `ts`,
// no backend involved. sameDay decides when a new divider pill is shown.
const sameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() &&
         da.getMonth()    === db.getMonth()    &&
         da.getDate()     === db.getDate();
};
const dayLabel = iso => {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const startOf = x => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff > 1 && diff < 7) return d.toLocaleDateString([], { weekday:"long" });
  return d.toLocaleDateString([], {
    day:"2-digit", month:"short",
    ...(d.getFullYear() !== now.getFullYear() ? { year:"numeric" } : {}),
  });
};
const fmtSize = bytes => {
  if (!bytes) return "";
  if (bytes < 1024)    return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes/1024).toFixed(1)} KB`;
  return `${(bytes/1048576).toFixed(1)} MB`;
};

// File-type → lucide icon component (was emoji glyphs). Rendered as <Icon/> in
// the FileAttachment chip; image/video messages render inline and never hit these.
const FILE_ICONS = { image: FileImage, video: FileVideo, document: FileText, email: Mail, other: FileIcon };

// ── Emoji-only detection (pure display) ──────────────────────────────────────
// A message renders "jumbo" (large, no bubble) when, after trimming, it is
// composed solely of emoji / emoji-components / ZWJ / VS16 / whitespace AND
// contains at least one pictographic char (so "123" or "#" alone never match —
// digits and # / * carry Emoji_Component). Capped at ≤ 8 emoji graphemes.
const EMOJI_ONLY_RE = new RegExp("^(?:\\p{Extended_Pictographic}|\\p{Emoji_Component}|\\u200d|\\ufe0f|\\s)+$", "u");
const countGraphemes = str => {
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    return [...new Intl.Segmenter(undefined, { granularity:"grapheme" }).segment(str)].length;
  }
  return [...str].length; // fallback: may over-count ZWJ sequences
};
const isJumboEmoji = text => {
  const t = (text || "").trim();
  if (!t) return false;
  if (!EMOJI_ONLY_RE.test(t)) return false;
  if (!/\p{Extended_Pictographic}/u.test(t)) return false;
  const n = countGraphemes(t.replace(/\s+/g, "")); // strip whitespace before counting
  return n >= 1 && n <= 8;
};

// Render text with clickable URLs and bold **text**
function RenderText({ text, isDone }) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  // split() needs the /g/ regex; the per-part check must NOT reuse it.
  // RegExp.test on a /g/ regex advances lastIndex and resumes from there
  // on the next call, so every other URL in a message tested false and
  // rendered as plain text. Harmless while all it cost was a hyperlink;
  // it now decides whether a recording link is intercepted, so it is a
  // fresh, stateless check.
  const isUrl = p => /^https?:\/\//.test(p);
  return (
    <span style={{ textDecoration: isDone ? "line-through" : "none", opacity: isDone ? 0.5 : 1 }}>
      {parts.map((part, i) =>
        isUrl(part) ? (
          isRecordingLink(part) ? (
            <button key={i} type="button" onClick={openRecordingsPanel}
              style={{ color:"#00FF99", textDecoration:"underline", background:"none",
                       border:"none", padding:0, font:"inherit", cursor:"pointer" }}>
              📼 Open in Recordings
            </button>
          ) : (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer"
              style={{ color:"#00FF99", textDecoration:"underline", wordBreak:"break-all" }}
              onClick={e => { e.stopPropagation(); }}>
              {part.length > 60 ? "📎 Open link" : part}
            </a>
          )
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

function FileAttachment({ msg }) {
  const Icon = FILE_ICONS[msg.file_type] || FileIcon;
  const url  = msg.file_url ? `${API_BASE}${msg.file_url}` : null;

  // Deleted-for-both state — no live file. Non-clickable muted chip that keeps
  // the original name plus a subtle "file deleted" hint.
  if (!url && (msg.file_deleted_at || msg.file_name)) return (
    <div className="flex items-center gap-3 rounded-xl"
      style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", padding:"10px 12px", minWidth:"180px" }}>
      <Icon size={22} strokeWidth={1.8} className="flex-shrink-0" style={{ opacity:0.45, color:"rgba(255,255,255,0.5)" }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate" style={{ color:"rgba(255,255,255,0.5)" }}>{msg.file_name || "File"}</p>
        <p className="text-[10px]" style={{ color:"rgba(255,255,255,0.3)", fontStyle:"italic" }}>file deleted</p>
      </div>
    </div>
  );

  if (msg.file_type === "image" && url) return (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img src={url} alt={msg.file_name}
        className="max-w-full rounded-xl border border-white/10 max-h-48 object-cover block" />
      {msg.file_name && <p className="text-[10px] text-white/30 mt-1">{msg.file_name}</p>}
    </a>
  );
  if (msg.file_type === "video" && url) return (
    <div>
      <video controls src={url} className="max-w-full rounded-xl border border-white/10 max-h-48" />
      {msg.file_name && <p className="text-[10px] text-white/30 mt-1">{msg.file_name}</p>}
    </div>
  );
  return (
    <a href={url} target="_blank" rel="noreferrer" download={msg.file_name}
      className="flex items-center gap-3 rounded-xl no-underline group"
      style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", padding:"10px 12px", minWidth:"180px" }}>
      <Icon size={22} strokeWidth={1.8} className="flex-shrink-0" style={{ color:"rgba(255,255,255,0.7)" }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white truncate">{msg.file_name || "File"}</p>
        <p className="text-[10px] text-white/35">{fmtSize(msg.file_size)}</p>
        {msg.converted_from && <p className="text-[10px]" style={{ color:NEON }}>Converted ✓</p>}
      </div>
      <Download size={16} strokeWidth={1.8} className="text-white/30 group-hover:text-white transition flex-shrink-0" />
    </a>
  );
}

function ConversionToast({ file, suggestion, onAccept, onDismiss }) {
  return (
    <div style={{
      position:"absolute", bottom:"80px", left:"50%", transform:"translateX(-50%)",
      zIndex:50, width:"400px", maxWidth:"90%",
      background:"#0d0d0d", border:`1px solid ${NEON}40`,
      borderRadius:"16px", padding:"16px 18px",
      boxShadow:"0 8px 32px rgba(0,0,0,0.7)",
    }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:"10px" }}>
        <AlertTriangle size={20} strokeWidth={1.8} style={{ flexShrink:0, color:"#facc15" }} />
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ color:"#fff", fontSize:"13px", fontWeight:"600", marginBottom:"3px" }}>Unsupported file type</p>
          <p style={{ color:"rgba(255,255,255,0.45)", fontSize:"12px", marginBottom:"10px", lineHeight:"1.5" }}>
            <strong style={{ color:"rgba(255,255,255,0.75)" }}>{file.name}</strong> can't be sent directly.
            {suggestion ? ` Convert to ${suggestion.target_label} for best compatibility?` : " This file type isn't supported."}
          </p>
          <div style={{ display:"flex", gap:"8px" }}>
            {suggestion && (
              <button onClick={onAccept}
                style={{ padding:"6px 14px", borderRadius:"8px", background:NEON, color:"#000", fontSize:"12px", fontWeight:"700", border:"none", cursor:"pointer" }}>
                Convert to {suggestion.target_label}
              </button>
            )}
            <button onClick={onDismiss}
              style={{ padding:"6px 14px", borderRadius:"8px", background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.5)", fontSize:"12px", border:"1px solid rgba(255,255,255,0.1)", cursor:"pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DirectMessageThread({ targetUser, onStartCall, highlightMsgId, onHighlightDone, onMarkDone, filesBus }) {
  const { user: me }        = useContext(AuthContext);
  const { addTaggedMessage } = useTaggedMessages();

  const [messages,   setMessages]   = useState([]);
  const [callEntries, setCallEntries] = useState([]);   // missed/declined CallLog rows for this peer
  const [doneMsgIds,  setDoneMsgIds]  = useState(new Set());   // ids marked done/deleted from right rail
  const [deletedMsgIds, setDeletedMsgIds] = useState(new Set()); // ids deleted from right rail

  // Expose handlers so RightRail can push done/delete events into this thread
  React.useEffect(() => {
    if (!onMarkDone) return;
    onMarkDone.current = {
      done:    (id) => setDoneMsgIds(prev => new Set([...prev, id])),
      deleted: (id) => setDeletedMsgIds(prev => new Set([...prev, id])),
    };
  }, [onMarkDone]);
  const [input,      setInput]      = useState("");
  const [loading,    setLoading]    = useState(false);
  const [sending,    setSending]    = useState(false);
  const [stagedFile, setStagedFile] = useState(null);
  const [convToast,  setConvToast]  = useState(null);
  const [uploadPct,  setUploadPct]  = useState(null);
  const wsRef     = useRef(null);
  const bottomRef = useRef(null);
  const pingRef   = useRef(null);

  const myId     = String(me?.id || "");
  const myName   = me?.full_name || me?.name || "You";
  const theirId  = String(targetUser?.id || "");
  const theirName = targetUser?.full_name || targetUser?.name || "Unknown";
  const dmKey    = [myId, theirId].sort().join("_");

  // ── Normalise any message shape from REST or WS into a consistent object ──
  const normaliseMsg = useCallback(m => ({
    id:             String(m.id || Math.random()),
    text:           m.content || m.text || "",
    self:           String(m.sender_id || m.from_id || "") === myId,
    name:           String(m.sender_id || m.from_id || "") === myId ? myName : theirName,
    ts:             m.created_at || m.ts || new Date().toISOString(),
    tag:            (m.tag && typeof m.tag === "string" && m.tag !== "conversation") ? m.tag : undefined,
    file_url:       m.file_url    || null,
    file_name:      m.file_name   || null,
    file_size:      m.file_size   || null,
    file_mime:      m.file_mime   || null,
    file_type:      m.file_type   || null,
    file_deleted_at: m.file_deleted_at || null,
    converted_from: m.converted_from || null,
    is_done:        m.is_done || false,
    message_type:   m.message_type || "text",
    meta:           m.meta || null,
  }), [myId, myName, theirName]);

  // ── Load full message history from REST on mount / peer change ──
  // This is the source of truth for persistence — messages always come from DB on refresh
  useEffect(() => {
    if (!theirId) return;
    setMessages([]);
    setLoading(true);
    fetch(`${API_BASE}/api/dm/messages?peer_id=${theirId}&limit=100`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(r => r.ok ? r.json() : { messages: [] })
      .then(d => {
        const msgs = d?.messages || d?.data || (Array.isArray(d) ? d : []);
        setMessages(msgs.map(normaliseMsg));
      })
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [theirId, normaliseMsg]);

  // ── Missed/declined calls with this peer → rendered as thread entries (spec 04) ──
  // Fetched on peer change; new entries surface next time the thread is opened.
  useEffect(() => {
    if (!theirId) { setCallEntries([]); return; }
    setCallEntries([]);
    fetch(`${API_BASE}/api/calling/logs`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(r => (r.ok ? r.json() : { logs: [] }))
      .then(d => {
        const rows = (d?.logs || [])
          .filter(l => String(l.other_id) === theirId && (l.status === "missed" || l.status === "declined"));
        setCallEntries(rows.map(l => ({
          id:            `call-${l.id}`,
          ts:            l.started_at,
          self:          l.direction === "outgoing",
          message_type:  "call_event",
          call: { status: l.status, call_type: l.call_type, direction: l.direction, other_name: l.other_name, ts: l.started_at },
        })));
      })
      .catch(() => setCallEntries([]));
  }, [theirId]);

  // Messages + call entries, one timeline sorted by ts.
  const timeline = useMemo(
    () => [...messages, ...callEntries].sort((a, b) => new Date(a.ts) - new Date(b.ts)),
    [messages, callEntries],
  );

  // ── WebSocket — receive-only for real-time incoming messages ──
  // We NEVER send messages via WS. The backend WS handler saves to DB and would
  // create duplicate records if we sent here too. REST /api/dm/send handles everything.
  useEffect(() => {
    if (!theirId) return;
    const token = localStorage.getItem("token");
    const ws = new WebSocket(`${WS_BASE}/api/dm/ws/${dmKey}?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type:"ping" }));
      }, 25000);
    };

    ws.onmessage = e => {
      try {
        const data = JSON.parse(e.data);
        // Real-time tag status update — flips the pill in the other party's
        // thread when sender/receiver hits Done or Delete-tag in the rail.
        if (data.type === "tag_status_update" && data.id) {
          setMessages(prev => prev.map(m =>
            m.id === data.id ? { ...m, tag_status: data.tag_status } : m
          ));
          return;
        }
        // Shared file deleted-for-both — null the bubble's file and notify the
        // Shared-files rail panel so its row is removed on both sides.
        if (data.type === "file_deleted" && data.message_id) {
          setMessages(prev => prev.map(m =>
            m.id === data.message_id ? { ...m, file_url: null, file_deleted_at: new Date().toISOString() } : m
          ));
          filesBus?.current?.onFileDeleted?.(data.message_id);
          return;
        }
        if (data.type !== "message") return;
        const incoming = normaliseMsg(data);
        // Only add messages from the other person — our own arrive via REST confirm
        if (incoming.self) return;
        setMessages(prev => {
          if (prev.some(m => m.id === incoming.id)) return prev; // dedupe
          return [...prev, incoming];
        });
        // Surface received attachments to the Shared-files rail panel (raw
        // payload carries file_mime / sender_id / ts the panel needs).
        if (data.file_url && data.file_name) filesBus?.current?.onFileMessage?.(data);
        // Push tagged incoming messages to right rail in real-time
        if (incoming.tag && incoming.tag !== "conversation") {
          addTaggedMessage({
            id:         incoming.id,
            sender_id:  incoming.self ? myId : theirId,
            text:       incoming.text,
            tag:        incoming.tag,
            senderName: incoming.name,
            ts:         incoming.ts,
            is_done:    false,
          });
        }
      } catch(_) {}
    };

    ws.onerror  = () => {};
    ws.onclose  = () => clearInterval(pingRef.current);

    return () => {
      clearInterval(pingRef.current);
      ws.close();
    };
  }, [theirId, dmKey, normaliseMsg]);

  // ── Scroll to bottom ──
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  // ── Scroll to highlighted message (from tagged message navigation) ──
  useEffect(() => {
    if (!highlightMsgId) return;
    const el = document.getElementById(`dm-msg-${highlightMsgId}`);
    if (el) {
      el.scrollIntoView({ behavior:"smooth", block:"center" });
      onHighlightDone?.();
    }
  }, [highlightMsgId, messages, onHighlightDone]);

  // ── File check before staging ──
  const handleFileChosen = async (file) => {
    if (!file) return;
    const mime = file.type || "application/octet-stream";
    try {
      const res  = await fetch(`${API_BASE}/api/dm/file-check`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ filename:file.name, mime }),
      });
      const data = await res.json();
      if (!data.supported) { setConvToast({ file, suggestion:data.conversion_suggestion }); return; }
    } catch(_) {}
    setStagedFile(file);
  };

  // ── Send ──────────────────────────────────────────────────────────────────
  // ALWAYS uses REST. The backend /api/dm/send:
  //   1. Saves to DB (persistence)
  //   2. Broadcasts to recipient via WS if they're online (real-time delivery)
  // We never send via WS directly — that would create a duplicate DB record.
  const send = async (e, tag = "none") => {
    // Guard: e may be a DOM event (from button click) — extract tag safely
    if (e && typeof e === "string") { tag = e; }
    if (tag && typeof tag !== "string") { tag = "none"; }

    const text = input.trim();
    if ((!text && !stagedFile) || sending) return;
    setSending(true);
    setInput("");

    // Track which path we're in so the outer catch can show the right toast
    let phase = "text";
    let optimisticTextId = null;

    try {
      if (stagedFile) {
        phase = "upload";
        // ── File upload ──
        const fileToSend = stagedFile;
        setStagedFile(null);
        setUploadPct(0);
        const fd = new FormData();
        fd.append("to_user_id", theirId);
        fd.append("text",       text);
        if (tag !== "none") fd.append("tag", tag);
        fd.append("file",       fileToSend);
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${API_BASE}/api/dm/upload`);
          xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("token")}`);
          xhr.upload.onprogress = ev => {
            if (ev.lengthComputable) setUploadPct(Math.round(ev.loaded / ev.total * 100));
          };
          xhr.onload = () => {
            setUploadPct(null);
            if (xhr.status === 200) {
              const saved = JSON.parse(xhr.responseText);
              setMessages(prev => [...prev, normaliseMsg(saved)]);
              // Surface my own upload to the Shared-files rail panel.
              if (saved.file_url && saved.file_name) filesBus?.current?.onFileMessage?.(saved);
              resolve();
            } else reject(new Error(xhr.responseText));
          };
          xhr.onerror = reject;
          xhr.send(fd);
        });

      } else {
        // ── Text message via REST ──
        // Show optimistic bubble immediately
        const optId = `opt_${Date.now()}`;
        optimisticTextId = optId;
        const optimistic = {
          id: optId, text, self:true, name:myName,
          ts: new Date().toISOString(),
          tag: tag !== "none" ? tag : undefined,
          file_url:null, file_name:null, file_size:null, file_type:null,
        };
        setMessages(prev => [...prev, optimistic]);

        const res = await fetch(`${API_BASE}/api/dm/send`, {
          method:  "POST",
          headers: { "Content-Type":"application/json", Authorization:`Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify({
            to_user_id: theirId,
            text,
            tag: tag !== "none" ? tag : undefined,
          }),
        });

        if (res.ok) {
          const saved = await res.json();
          // Replace optimistic with confirmed server message.
          // Preserve the local tag — backend msg_to_dict doesn't include it.
          const confirmed = { ...normaliseMsg(saved), tag: tag !== "none" ? tag : undefined };
          setMessages(prev => prev.map(m => m.id === optId ? confirmed : m));

          // Push to right rail in real-time if message has a tag
          if (tag !== "none" && confirmed.id) {
            addTaggedMessage({
              id:         confirmed.id,
              sender_id:  myId,
              text:       confirmed.text,
              tag:        tag,
              senderName: myName,
              ts:         confirmed.ts,
              is_done:    false,
            });
          }

          // Tagged messages appear in the RECIPIENT's right rail only.
          // The backend stores the tag and the recipient's RightRail loads it on their session.
        } else {
          // Remove optimistic on failure
          const errText = await res.text().catch(() => res.status);
          console.error("dm_send failed:", errText);
          setMessages(prev => prev.filter(m => m.id !== optId));
          toast.error("Message failed to send");
        }
      }
    } catch(err) {
      console.error("Send failed:", err);
      if (phase === "upload") {
        toast.error("Upload failed");
      } else {
        // Remove the optimistic bubble (if it's still there) and notify
        if (optimisticTextId) {
          setMessages(prev => prev.filter(m => m.id !== optimisticTextId));
        }
        toast.error("Message failed to send");
      }
    } finally {
      setSending(false);
    }
  };

  // ── Start a call — room + invite + busy handling live in the app-wide
  //    CallProvider (provider.startCall via onStartCall). ──
  const startCall = async (audioOnly) => {
    try {
      await onStartCall?.({ targetUser, audioOnly });
    } catch(err) {
      console.error("Failed to start call:", err);
      toast.error("Could not start call");
    }
  };

  if (!targetUser) return null;
  const inits = ini(theirName);

  return (
    <div className="flex flex-col h-full bg-[#0B0B0B]" style={{ position:"relative" }}>

      {convToast && (
        <ConversionToast
          file={convToast.file}
          suggestion={convToast.suggestion}
          onAccept={() => { setStagedFile(convToast.file); setConvToast(null); }}
          onDismiss={() => setConvToast(null)}
        />
      )}

      {/* Header — floating rounded pill (in-flow, self-start so it does not span
          the full column; no bottom border; ~12px margin all sides). */}
      <div className="flex-shrink-0" style={{
        alignSelf:    "flex-start",
        display:      "inline-flex",
        alignItems:   "center",
        gap:          8,
        margin:       12,
        background:   "#0d0d0d",
        border:       "1px solid rgba(255,255,255,0.10)",
        borderRadius: 999,
        padding:      "8px 10px 8px 8px",
      }}>
        <div className="flex items-center gap-3">
          <div className="relative">
            {targetUser.avatar || targetUser.profile_picture ? (
              <img src={targetUser.avatar || targetUser.profile_picture} alt={theirName}
                className="w-9 h-9 rounded-full object-cover"
                style={{ border: "1.5px solid rgba(0,255,153,0.4)" }} />
            ) : (
              <div style={{
                width:          36,
                height:         36,
                borderRadius:   "50%",
                background:     "#0d0d0d",
                border:         "1.5px solid rgba(0,255,153,0.4)",
                color:          "#00FF99",
                fontSize:       13,
                fontWeight:     600,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                textTransform:  "uppercase",
                flexShrink:     0,
              }}>
                {inits}
              </div>
            )}
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#00FF99] border-2 border-black" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{theirName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <style>{IB_CSS}</style>
          <button onClick={() => startCall(true)} title="Audio call"
            className="kw-ib" style={IB_STYLE}>
            <Phone size={16} strokeWidth={1.8} />
          </button>
          <button onClick={() => startCall(false)} title="Video call"
            className="kw-ib" style={IB_STYLE}>
            <Video size={16} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* Message list */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 rounded-full border-2 border-[#00FF99] border-t-transparent animate-spin" />
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div style={{
              width:          56,
              height:         56,
              borderRadius:   "50%",
              background:     "#0d0d0d",
              border:         "1.5px solid rgba(0,255,153,0.4)",
              color:          "#00FF99",
              fontSize:       20,
              fontWeight:     600,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              textTransform:  "uppercase",
            }}>
              {inits}
            </div>
            <p className="text-white/40 text-sm">
              Start a conversation with <span className="text-white/70 font-medium">{theirName}</span>
            </p>
          </div>
        )}

        {timeline.map((m, i) => {
          const showDay  = i === 0 || !sameDay(timeline[i-1].ts, m.ts);
          if (m.message_type === "call_event") {
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div style={{ display:"flex", justifyContent:"center", margin:"6px 0" }}>
                    <span style={{ fontSize:10.5, color:"rgba(255,255,255,0.35)", background:"#0d0d0d", border:"1px solid rgba(255,255,255,0.10)", borderRadius:99, padding:"4px 12px" }}>{dayLabel(m.ts)}</span>
                  </div>
                )}
                <CallThreadEntry
                  entry={m.call}
                  onMessage={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
                  onCallBack={(audio) => onStartCall?.({ targetUser: { id: theirId, full_name: theirName }, audioOnly: audio })}
                />
              </React.Fragment>
            );
          }
          const showName = i === 0 || timeline[i-1].self !== m.self;
          const jumbo    = !!(m.text && !m.file_url && m.message_type !== "kudos" && isJumboEmoji(m.text));
          return (
            <React.Fragment key={m.id}>
            {showDay && (
              <div style={{ display:"flex", justifyContent:"center", margin:"6px 0" }}>
                <span style={{
                  fontSize:10.5, color:"rgba(255,255,255,0.35)",
                  background:"#0d0d0d", border:"1px solid rgba(255,255,255,0.10)",
                  borderRadius:99, padding:"4px 12px",
                }}>{dayLabel(m.ts)}</span>
              </div>
            )}
            <div
              id={`dm-msg-${m.id}`}
              className={`flex flex-col ${m.self ? "items-end" : "items-start"} w-full`}
              style={{
                transition:"background 0.5s",
                background: highlightMsgId === m.id ? "rgba(0,255,153,0.08)" : "transparent",
                borderRadius: 12,
                padding: "2px 4px",
              }}>
              {/* Tag pill — three states driven by tag_status (backend) with
                  optimistic-local fallbacks so the actor sees the update
                  immediately while the WebSocket round-trips for the peer. */}
              {m.tag && TAG_COLORS[m.tag] && (() => {
                const status    = m.tag_status;
                const isDone    = status === "done"    || doneMsgIds.has(m.id) || m.is_done;
                const isDeleted = status === "deleted" || deletedMsgIds.has(m.id);

                if (isDone) {
                  return (
                    <span style={{
                      display:"inline-flex", alignItems:"center", gap:4,
                      fontSize:10, fontWeight:600, letterSpacing:"0.05em",
                      padding:"2px 8px", borderRadius:20, marginBottom:3,
                      background:"rgba(0,255,153,0.1)",
                      border:"1px solid rgba(0,255,153,0.3)",
                      color:"rgba(0,255,153,0.8)",
                      textTransform:"capitalize",
                      transition:"all 0.3s",
                    }}>
                      <CheckCircle size={11} strokeWidth={2.5} />
                      {m.tag}
                    </span>
                  );
                }
                if (isDeleted) {
                  return (
                    <div style={{
                      display:"inline-flex", flexDirection:"column",
                      alignItems:"flex-start", marginBottom:3,
                    }}>
                      <span style={{
                        display:"inline-flex", alignItems:"center", gap:4,
                        fontSize:10, fontWeight:600, letterSpacing:"0.05em",
                        padding:"2px 8px", borderRadius:20,
                        background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.08)",
                        color:"rgba(255,255,255,0.25)",
                        textTransform:"capitalize",
                        textDecoration:"line-through",
                        transition:"all 0.3s",
                      }}>
                        {m.tag}
                      </span>
                      <span style={{
                        fontSize:9, color:"rgba(255,255,255,0.25)",
                        marginTop:2, paddingLeft:8, fontStyle:"italic",
                      }}>
                        Dismissed
                      </span>
                    </div>
                  );
                }
                // pending — original coloured pill style
                return (
                  <span style={{
                    display:"inline-flex", alignItems:"center", gap:4,
                    fontSize:10, fontWeight:600, letterSpacing:"0.05em",
                    padding:"2px 8px", borderRadius:20, marginBottom:3,
                    background:`${TAG_COLORS[m.tag]}18`,
                    color: TAG_COLORS[m.tag],
                    border:`1px solid ${TAG_COLORS[m.tag]}40`,
                    textTransform:"capitalize",
                    transition:"all 0.3s",
                  }}>
                    <TagIcon size={10} strokeWidth={2.5} />
                    {m.tag}
                  </span>
                );
              })()}
              {showName && (
                <span className="text-[10px] text-white/30 mb-1 px-1">{m.name}</span>
              )}
              <div className={`flex items-end gap-2 max-w-[75%] ${m.self ? "flex-row-reverse" : "flex-row"}`}>
                {!m.self && (
                  <div className="flex-shrink-0 self-end mb-0.5">
                    {showName
                      ? <div style={{
                          width:          24,
                          height:         24,
                          borderRadius:   "50%",
                          background:     "#0d0d0d",
                          border:         "1px solid rgba(0,255,153,0.4)",
                          color:          "#00FF99",
                          fontSize:       9,
                          fontWeight:     600,
                          display:        "flex",
                          alignItems:     "center",
                          justifyContent: "center",
                          textTransform:  "uppercase",
                        }}>{ini(theirName)}</div>
                      : <div className="w-6" />
                    }
                  </div>
                )}
                {m.message_type === "kudos" && m.meta ? (
                  <div style={{ maxWidth: 260 }}>
                    <KudosBanner
                      type={m.meta.type}
                      name={m.meta.to_name || theirName}
                      message={m.meta.message}
                      fromName={m.meta.from_name}
                      compact
                    />
                  </div>
                ) : jumbo ? (
                  // Emoji-only message — large, no bubble background/border.
                  <div style={{
                    fontSize:     64,
                    lineHeight:   1.15,
                    background:   "transparent",
                    border:       "none",
                    padding:      0,
                    wordBreak:    "break-word",
                    overflowWrap: "break-word",
                    maxWidth:     "100%",
                  }}>
                    <RenderText text={m.text} isDone={m.is_done} />
                  </div>
                ) : (
                  <div
                    className={`px-3.5 py-2.5 text-sm leading-relaxed break-words overflow-hidden ${
                      m.self ? "rounded-2xl rounded-br-sm" : "rounded-2xl rounded-bl-sm"
                    }`}
                    style={{
                      background:   m.self ? "rgba(0,255,153,0.12)" : "#131313",
                      border:       m.self ? "1px solid rgba(0,255,153,0.30)" : "1px solid rgba(255,255,255,0.10)",
                      color:        m.self ? "#fff" : "rgba(255,255,255,0.9)",
                      wordBreak:    "break-word",
                      overflowWrap: "break-word",
                      minWidth:     0,
                      maxWidth:     "100%",
                    }}>
                    {(m.file_url || m.file_deleted_at) && <FileAttachment msg={m} />}
                    {m.text && m.file_url && <p className="mt-1.5 text-sm">{m.text}</p>}
                    {m.text && !m.file_url && <RenderText text={m.text} isDone={m.is_done} />}
                  </div>
                )}
              </div>
              <span className="text-white/20 mt-0.5 px-1" style={{ fontSize:9.5 }}>{niceTime(m.ts)}</span>
            </div>
            </React.Fragment>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input — no divider/border above; thread + composer share one surface.
          The composer's own rounded pill border lives inside MessageInputBox. */}
      <div className="px-4 py-3 flex-shrink-0">
        <MessageInputBox
          value={input}
          onChange={setInput}
          onSend={send}
          placeholder={`Message ${theirName}…`}
          sending={sending}
          onFileChosen={handleFileChosen}
          stagedFile={stagedFile}
          onRemoveFile={() => setStagedFile(null)}
          uploadPct={uploadPct}
        />
      </div>
    </div>
  );
}
